import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Briefcase, Zap, Filter, MapPin } from 'lucide-react';
import api from '../api';
import { calculateMatchScore } from '../utils/scoringUtils';

const Analytics = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({});

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(storedUser);

        api.get('/jobs')
            .then(res => {
                setJobs(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to load jobs for analytics", err);
                setLoading(false);
            });
    }, []);

    const metrics = useMemo(() => {
        if (!jobs.length) return null;

        // 1. Overview Stats
        const totalJobs = jobs.length;
        const matches = jobs.map(job => calculateMatchScore(job, user));
        const strongMatches = matches.filter(m => m.score >= 70).length;

        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const newJobs = jobs.filter(j => new Date(j.date_posted) > oneWeekAgo).length;

        // 2. Location Data (Enhanced Normalization)
        const locMap = {};
        jobs.forEach(j => {
            let rawLoc = j.location;
            if (typeof j.location === 'object') {
                rawLoc = j.location.city || j.location.region || j.location.countryName || 'Remote';
            }
            // Clean up string: "New York, NY" -> "New York", "London/Remote" -> "London"
            const cleanLoc = (rawLoc || 'Remote')
                .toString()
                .split(',')[0]
                .split('/')[0]
                .trim();

            // Capitalize first letter of each word
            const normalizedLoc = cleanLoc.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

            locMap[normalizedLoc] = (locMap[normalizedLoc] || 0) + 1;
        });
        const locationData = Object.entries(locMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);

        // 3. Experience Data
        const expMap = {};
        jobs.forEach(j => {
            let lvl = 'Mid Level'; // Default
            const title = (j.title || '').toLowerCase();
            const desc = (j.description || '').toLowerCase();
            const exp = (j.experience || '').toLowerCase();

            if (title.includes('senior') || title.includes('lead') || title.includes('principal') || title.includes('staff') || exp.includes('senior')) lvl = 'Senior Level';
            else if (title.includes('junior') || title.includes('entry') || title.includes('associate') || exp.includes('junior')) lvl = 'Entry Level';
            else if (title.includes('intern') || exp.includes('intern')) lvl = 'Internship';
            else if (title.includes('manager') || title.includes('head') || title.includes('director')) lvl = 'Management';
            else if (desc.includes('5+') || desc.includes('5 years') || desc.includes('senior')) lvl = 'Senior Level';

            expMap[lvl] = (expMap[lvl] || 0) + 1;
        });
        const experienceData = Object.entries(expMap).map(([name, value]) => ({ name, value }));

        // 4. Skills Heatmap (Smart Aggregation)
        const skillMap = {};
        // Dictionary for canonical casing
        const canonicalSkills = {
            'react': 'React', 'reactjs': 'React', 'react.js': 'React',
            'node': 'Node.js', 'nodejs': 'Node.js', 'node.js': 'Node.js',
            'js': 'JavaScript', 'javascript': 'JavaScript', 'typescript': 'TypeScript', 'ts': 'TypeScript',
            'aws': 'AWS', 'amazon web services': 'AWS',
            'python': 'Python', 'py': 'Python',
            'java': 'Java',
            'c#': 'C#', '.net': '.NET',
            'go': 'Go', 'golang': 'Go',
            'sql': 'SQL', 'mysql': 'SQL', 'postgres': 'SQL', 'postgresql': 'SQL',
            'docker': 'Docker', 'k8s': 'Kubernetes', 'kubernetes': 'Kubernetes',
            'vue': 'Vue.js', 'vuejs': 'Vue.js',
            'angular': 'Angular',
            'html': 'HTML', 'css': 'CSS', 'tailwind': 'Tailwind'
        };

        jobs.forEach(j => {
            const desc = (j.description || '').toLowerCase();
            const techs = j.technologies || [];
            const combined = new Set();

            // Add from structured tags
            techs.forEach(t => combined.add(t.toLowerCase()));

            // Add from regex scan of description for common keywords
            Object.keys(canonicalSkills).forEach(key => {
                // Boundary check to avoid "go" matching "good"
                const regex = new RegExp(`\\b${key.replace('.', '\\.')}\\b`, 'i');
                if (regex.test(desc)) combined.add(key);
            });

            combined.forEach(k => {
                const polishedName = canonicalSkills[k] || (k.charAt(0).toUpperCase() + k.slice(1));
                skillMap[polishedName] = (skillMap[polishedName] || 0) + 1;
            });
        });

        const skillData = Object.entries(skillMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);

        return { totalJobs, strongMatches, newJobs, locationData, experienceData, skillData };
    }, [jobs, user]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981'];

    return (
        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Market Insights</h1>
                        <p className="text-slate-500 dark:text-slate-400">Real-time analysis of the job market based on current listings</p>
                    </div>
                    <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm font-medium">
                        <Zap className="w-4 h-4" />
                        Live Data
                    </div>
                </div>

                {/* Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <StatCard
                        title="Total Opportunities"
                        value={metrics?.totalJobs}
                        icon={Briefcase}
                        color="blue"
                    />
                    <StatCard
                        title="Strong Matches"
                        value={metrics?.strongMatches}
                        subtitle="70%+ Match Score"
                        icon={TrendingUp}
                        color="emerald"
                    />
                    <StatCard
                        title="New This Week"
                        value={metrics?.newJobs}
                        icon={Zap}
                        color="amber"
                    />
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* Location Trends */}
                    <ChartCard title="Top Hiring Locations">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={metrics?.locationData} layout="vertical" margin={{ left: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#cbd5e1" opacity={0.3} />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12, fill: '#64748b' }} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    cursor={{ fill: 'transparent' }}
                                />
                                <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* Experience Level */}
                    <ChartCard title="Experience Level Breakdown">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={metrics?.experienceData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {metrics?.experienceData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: '12px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap justify-center gap-4 mt-4">
                            {metrics?.experienceData.map((entry, index) => (
                                <div key={index} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                    {entry.name} ({entry.value})
                                </div>
                            ))}
                        </div>
                    </ChartCard>

                    {/* Skill Demand */}
                    <ChartCard title="Top In-Demand Skills" className="lg:col-span-2">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={metrics?.skillData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#cbd5e1" opacity={0.3} />
                                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <YAxis hide />
                                <Tooltip
                                    cursor={{ fill: '#f1f5f9', opacity: 0.4 }}
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                />
                                <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </motion.div>
        </div>
    );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
        emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
        amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
    }[color] || 'bg-slate-50 text-slate-600';

    return (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{title}</p>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                        {value}
                    </h3>
                    {subtitle && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-xl ${colorClasses}`}>
                    <Icon className="w-6 h-6" />
                </div>
            </div>
        </div>
    );
};

const ChartCard = ({ title, children, className = '' }) => (
    <div className={`bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 ${className}`}>
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-6">{title}</h3>
        {children}
    </div>
);

export default Analytics;
